import React, { useState, useEffect } from 'react';

import AgModalWindow from '@/components/common/ModalWindow/AgModalWindow';
import Button, { ButtonStyleType } from '@/components/common/Button/Button';
import { EXPECTED_FIELDS, autoMapColumns, validateMappings, ExpectedField, toSnakeCase } from '../types/columnMapping';
import styles from './column-mapping-modal.module.scss';

interface ColumnMappingModalProps {
  isOpen: boolean;
  csvHeaders: string[];
  sampleData: string[][];
  onConfirm: (columnMappings: Map<string, string>) => void;
  onCancel: () => void;
}

export default function ColumnMappingModal({
  isOpen,
  csvHeaders,
  sampleData,
  onConfirm,
  onCancel
}: ColumnMappingModalProps) {
  const [mappings, setMappings] = useState<Map<string, string>>(new Map());
  const [errors, setErrors] = useState<string[]>([]);
  const [isMaximized, setIsMaximized] = useState(false);

  useEffect(() => {
    if (csvHeaders.length > 0) {
      const autoMapped = autoMapColumns(csvHeaders);
      setMappings(autoMapped);
    }
  }, [csvHeaders]);

  const handleMappingChange = (csvColumn: string, targetField: string | null) => {
    const newMappings = new Map(mappings);
    if (targetField === null || targetField === '') {
      newMappings.delete(csvColumn);
    } else {
      newMappings.set(csvColumn, targetField);
    }
    setMappings(newMappings);
    setErrors([]);
  };

  const handleConfirm = () => {
    const validation = validateMappings(mappings);
    if (!validation.valid) {
      setErrors(validation.errors);
      return;
    }
    
    const allColumnMappings = new Map<string, string>();
    
    csvHeaders.forEach(header => {
      const mappedField = mappings.get(header);
      if (mappedField) {
        allColumnMappings.set(header, mappedField);
      } else {
        allColumnMappings.set(header, toSnakeCase(header));
      }
    });
    
    onConfirm(allColumnMappings);
  };

  const getAvailableFields = (currentCsvColumn: string): ExpectedField[] => {
    const mappedFields = new Set(
      Array.from(mappings.entries())
        .filter(([col]) => col !== currentCsvColumn)
        .map(([, field]) => field)
    );
    return EXPECTED_FIELDS.filter(f => !mappedFields.has(f.name));
  };

  return (
    <AgModalWindow
      title="Map Columns"
      isOpen={isOpen}
      onClose={onCancel}
      contentStyle={{ 
        width: isMaximized ? '1200px' : '900px', 
        maxHeight: isMaximized ? '90vh' : '900px'
      }}
      maximizeState={{ isMaximized, setIsMaximized }}
    >
      <div className={styles['modal-content']}>
        <div className={styles.description}>
          Map your uploaded columns to the expected fields. Required fields are marked with *.
        </div>

        {errors.length > 0 && (
          <div className={styles['error-banner']}>
            {errors.map((error, idx) => (
              <div key={idx} className={styles['error-message']}>{error}</div>
            ))}
          </div>
        )}

        <div className={styles['mapping-container']}>
          <div className={styles['header-row']}>
            <div className={styles['column-header']}>Uploaded Column</div>
            <div className={styles['column-header']}>Sample Data</div>
            <div className={styles['column-header']}>Map To</div>
          </div>

          {csvHeaders.map((header, idx) => {
            const availableFields = getAvailableFields(header);
            const currentMapping = mappings.get(header);
            const sampleValues = sampleData.map(row => row[idx]).filter(v => v).slice(0, 3);
            
            return (
              <div key={header} className={styles['mapping-row']}>
                <div className={styles['csv-column']}>{header}</div>
                <div className={styles['sample-data']}>
                  {sampleValues.length > 0 ? sampleValues.join(', ') : '(empty)'}
                </div>
                <div className={styles['target-field']}>
                  <select
                    value={currentMapping || ''}
                    onChange={(e) => handleMappingChange(header, e.target.value || null)}
                    className={styles['field-select']}
                  >
                    <option value="">Skip this column</option>
                    {currentMapping && !availableFields.find(f => f.name === currentMapping) && (
                      <option value={currentMapping}>
                        {EXPECTED_FIELDS.find(f => f.name === currentMapping)?.displayName}
                      </option>
                    )}
                    {availableFields.map(field => (
                      <option key={field.name} value={field.name}>
                        {field.displayName}{field.required ? ' *' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            );
          })}
        </div>

        <div className={styles['action-buttons']}>
          <Button
            onClick={handleConfirm}
            buttonStyleType={ButtonStyleType.PRIMARY}
          >
            Confirm Mapping
          </Button>
          <Button
            onClick={onCancel}
            buttonStyleType={ButtonStyleType.SECONDARY}
            className="ml-10"
          >
            Cancel
          </Button>
        </div>
      </div>
    </AgModalWindow>
  );
}
